/**
 * Open a document-form route in its own pop-up window.
 *
 * Forms open as sized pop-up windows (Ola, 2026-09-26: "they should always open a
 * new window"). A form opened with a `bridge` always gets a NEW window, so several
 * forms can be open side by side.
 *
 * The bridge is what makes that safe. A form window gets its data and calls its
 * save handlers on the main page. Those used to be shared globals
 * (window.adsFormData, window.handleADSFormSave), owned by whichever form opened
 * last, so two open forms could save one client's form through the other's
 * handler. A reused window name hid this by allowing one editor at a time. Now
 * each window gets its own bridge object, registered under an id that travels in
 * the URL (?fw=<id>); the form window reads it with getFormBridge().
 *
 * IMPORTANT: window.open must keep `window.opener` (the form reads the bridge from
 * it), so never pass `noopener`.
 */
export const FORM_BRIDGE_PARAM = 'fw';
export const FORM_DRAFT_PARAM = 'draftId';
export const isFormDraftReference = id => typeof id === 'string' && /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,127}$/.test(id);

// Persist only an opaque server reference, never clinical content or identity.
export function rememberFormDraft(id, win = window) {
  if (!isFormDraftReference(id)) return;
  const url = new URL(win.location.href);
  url.searchParams.set(FORM_DRAFT_PARAM, id);
  win.history.replaceState(win.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}
const POPUP_FEATURES = 'width=1400,height=900,left=80,top=40,scrollbars=yes,resizable=yes';
const CLEANUP_INTERVAL_MS = 5000;

let sequence = 0;
const newId = () => `${Date.now().toString(36)}${(++sequence).toString(36)}${Math.random().toString(36).slice(2, 6)}`;

function withParam(url, key, value) {
  const [path, hash = ''] = url.split('#');
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}${key}=${encodeURIComponent(value)}${hash ? `#${hash}` : ''}`;
}

/** Drop a window's hand-off once the window is closed, so the registry does not grow. */
function releaseWhenClosed(win, id) {
  if (typeof window.setInterval !== 'function' || !win) return;
  const timer = window.setInterval(() => {
    if (!win || win.closed) {
      window.clearInterval(timer);
      if (window.__formBridges) delete window.__formBridges[id];
    }
  }, CLEANUP_INTERVAL_MS);
}

/**
 * @param {string} url      Route to open, e.g. '/ads-form'
 * @param {string} [name]   Window name. With a bridge it is only a prefix and the
 *                          window is always new; without one, a caller-chosen name
 *                          (e.g. one per document) reuses that document's window.
 * @param {{ bridge?: object }} [opts] Data and handlers this window may use.
 * @returns {Window|null}   The opened window, or null if the browser blocked it.
 */
export function openFormWindow(url, name = '_blank', { bridge, targetWindow, asTab = false } = {}) {
  const open = destination => {
    if (targetWindow) {
      if (targetWindow.closed) return null;
      targetWindow.location.replace(destination);
      return targetWindow;
    }
    return asTab ? window.open(destination, name || '_blank') : window.open(destination, name || '_blank', POPUP_FEATURES);
  };
  if (!bridge) return open(url);

  const id = newId();
  window.__formBridges = window.__formBridges || {};
  window.__formBridges[id] = bridge;
  const target = `${name && name !== '_blank' ? name : 'form'}_${id}`;
  let formUrl = withParam(url, FORM_BRIDGE_PARAM, id);
  if (url.split(/[?#]/)[0] === '/ads-form' && isFormDraftReference(bridge.adsFormData?.draftId)) {
    formUrl = withParam(formUrl, FORM_DRAFT_PARAM, bridge.adsFormData.draftId);
  }
  const win = targetWindow ? open(formUrl) : asTab ? window.open(formUrl, target) : window.open(formUrl, target, POPUP_FEATURES);
  if (!win) {
    delete window.__formBridges[id];
    return null;
  }
  releaseWhenClosed(win, id);
  return win;
}

/**
 * In the form window: this window's own hand-off from the main page.
 * A window opened with a bridge never falls back to the shared globals: if its
 * hand-off is gone (main page reloaded), it gets null and reports the lost
 * connection instead of saving through another form's handler.
 *
 * @param {Window} [win]
 * @returns {object|null}
 */
export function getFormBridge(win = window) {
  try {
    const opener = win?.opener;
    if (!opener || opener.closed) return null;
    const id = new URLSearchParams(win.location?.search || '').get(FORM_BRIDGE_PARAM);
    if (!id) return opener;
    return opener.__formBridges?.[id] || null;
  } catch {
    // A closed or cross-origin opener cannot supply this form's callbacks.
    return null;
  }
}

export default openFormWindow;
